import { useState, useRef, useEffect } from 'react';

const AYLAR = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];
const GUNLER = ['Pt','Sa','Ça','Pe','Cu','Ct','Pz'];

function AySecici({ ay, onChange }) {
  const [acik, setAcik] = useState(false);
  const ref = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setAcik(false); };
    if (acik) {
      document.addEventListener('mousedown', handler);
      setTimeout(() => {
        const el = listRef.current?.querySelector('[data-secili="true"]');
        el?.scrollIntoView({ block: 'center' });
      }, 0);
    }
    return () => document.removeEventListener('mousedown', handler);
  }, [acik]);

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setAcik(a => !a)}
        className="bg-white border border-gray-300 hover:border-indigo-400 text-gray-700 text-sm rounded-lg px-2 py-1 flex items-center gap-1 min-w-[80px] justify-between transition-colors">
        <span>{AYLAR[ay]}</span>
        <span className="text-xs opacity-60">▾</span>
      </button>
      {acik && (
        <div ref={listRef}
          className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl z-[300] overflow-y-auto"
          style={{ maxHeight: '200px', minWidth: '100px' }}>
          {AYLAR.map((a, i) => (
            <button key={i} data-secili={i === ay}
              onClick={() => { onChange(i); setAcik(false); }}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 hover:text-blue-700 transition-colors
                ${i === ay ? 'bg-blue-600 text-white font-bold' : 'text-gray-700'}`}>
              {a}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function YilSecici({ yil, yillar, onChange }) {
  const [acik, setAcik] = useState(false);
  const ref = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setAcik(false); };
    if (acik) {
      document.addEventListener('mousedown', handler);
      setTimeout(() => {
        const el = listRef.current?.querySelector('[data-secili="true"]');
        el?.scrollIntoView({ block: 'center' });
      }, 0);
    }
    return () => document.removeEventListener('mousedown', handler);
  }, [acik]);

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setAcik(a => !a)}
        className="bg-white border border-gray-300 hover:border-indigo-400 text-gray-700 text-sm rounded-lg px-2 py-1 flex items-center gap-1 min-w-[58px] justify-between transition-colors">
        <span>{yil}</span>
        <span className="text-xs opacity-60">▾</span>
      </button>
      {acik && (
        <div ref={listRef}
          className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl z-[300] overflow-y-auto"
          style={{ maxHeight: '200px', minWidth: '70px' }}>
          {yillar.map(y => (
            <button key={y} data-secili={y === yil}
              onClick={() => { onChange(y); setAcik(false); }}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 hover:text-blue-700 transition-colors
                ${y === yil ? 'bg-blue-600 text-white font-bold' : 'text-gray-700'}`}>
              {y}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * RezervasyonDatePicker — rezervasyon ekleme için özel takvim.
 *
 * Ek prop:
 *   doluAraliklar: [{ girisTarihi: ISO string, cikisTarihi: ISO string | null }]
 *     → bu aralıklara giren günler kırmızıyla gösterilir ve seçilemez.
 *     → cikisTarihi günü SERBEST sayılır (check-out günü yeni giriş yapılabilir).
 */
export default function RezervasyonDatePicker({
  value, onChange,
  placeholder = 'GG.AA.YYYY',
  className = '',
  minDate,
  doluAraliklar = [],
}) {
  const [acik, setAcik] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [gorunenAy, setGorunenAy] = useState(null);
  const triggerRef = useRef(null);
  const pickerRef = useRef(null);

  const parseValue = (v) => {
    if (!v) return null;
    const parts = v.split('.');
    if (parts.length !== 3) return null;
    const [g, a, y] = parts.map(Number);
    if (isNaN(g) || isNaN(a) || isNaN(y)) return null;
    return new Date(y, a - 1, g);
  };

  const seciliTarih = parseValue(value);
  const minTarih = parseValue(minDate);

  // Verilen gün dolu aralıklara giriyor mu?
  const isDolu = (gun) => {
    if (!gorunenAy || doluAraliklar.length === 0) return false;
    const d = new Date(gorunenAy.yil, gorunenAy.ay, gun);
    d.setHours(0, 0, 0, 0);
    return doluAraliklar.some(aralik => {
      const giris = new Date(aralik.girisTarihi);
      giris.setHours(0, 0, 0, 0);
      // cikisTarihi günü serbest — yeni rezervasyon başlayabilir
      if (!aralik.cikisTarihi) return d >= giris;
      const cikis = new Date(aralik.cikisTarihi);
      cikis.setHours(0, 0, 0, 0);
      return d >= giris && d < cikis;
    });
  };

  const isDisabled = (gun) => {
    if (!gorunenAy) return false;
    const d = new Date(gorunenAy.yil, gorunenAy.ay, gun);
    if (minTarih) {
      const min = new Date(minTarih.getFullYear(), minTarih.getMonth(), minTarih.getDate());
      if (d < min) return true;
    }
    return isDolu(gun);
  };

  useEffect(() => {
    if (acik) {
      const base = seciliTarih || new Date();
      setGorunenAy({ ay: base.getMonth(), yil: base.getFullYear() });
    }
  }, [acik]);

  useEffect(() => {
    const handler = (e) => {
      if (
        pickerRef.current && !pickerRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) setAcik(false);
    };
    if (acik) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [acik]);

  const handleOpen = () => {
    if (!acik) {
      const rect = triggerRef.current.getBoundingClientRect();
      const pickerH = 340;
      const top = rect.bottom + window.scrollY + 4;
      const adjustedTop = (rect.bottom + pickerH > window.innerHeight)
        ? rect.top + window.scrollY - pickerH - 4
        : top;
      setPos({ top: adjustedTop, left: rect.left + window.scrollX });
    }
    setAcik(a => !a);
  };

  const gunSec = (gun) => {
    if (isDisabled(gun)) return;
    const tarih = new Date(gorunenAy.yil, gorunenAy.ay, gun);
    const str = [
      String(tarih.getDate()).padStart(2, '0'),
      String(tarih.getMonth() + 1).padStart(2, '0'),
      tarih.getFullYear()
    ].join('.');
    onChange(str);
    setAcik(false);
  };

  const bugun = () => {
    const t = new Date();
    if (minTarih) {
      const bugunD = new Date(t.getFullYear(), t.getMonth(), t.getDate());
      const min = new Date(minTarih.getFullYear(), minTarih.getMonth(), minTarih.getDate());
      if (bugunD < min) return;
    }
    const str = [
      String(t.getDate()).padStart(2, '0'),
      String(t.getMonth() + 1).padStart(2, '0'),
      t.getFullYear()
    ].join('.');
    onChange(str);
    setAcik(false);
  };

  const takvimGunleri = () => {
    if (!gorunenAy) return [];
    const { ay, yil } = gorunenAy;
    const ilkGun = new Date(yil, ay, 1).getDay();
    const offset = ilkGun === 0 ? 6 : ilkGun - 1;
    const sonGun = new Date(yil, ay + 1, 0).getDate();
    const gunler = [];
    for (let i = 0; i < offset; i++) gunler.push(null);
    for (let i = 1; i <= sonGun; i++) gunler.push(i);
    return gunler;
  };

  const oncekiAy = () => setGorunenAy(g => g.ay === 0 ? { ay: 11, yil: g.yil - 1 } : { ay: g.ay - 1, yil: g.yil });
  const sonrakiAy = () => setGorunenAy(g => g.ay === 11 ? { ay: 0, yil: g.yil + 1 } : { ay: g.ay + 1, yil: g.yil });

  const isSecili = (gun) => {
    if (!seciliTarih || !gorunenAy) return false;
    return seciliTarih.getFullYear() === gorunenAy.yil &&
      seciliTarih.getMonth() === gorunenAy.ay &&
      seciliTarih.getDate() === gun;
  };

  const isToday = (gun) => {
    const t = new Date();
    return t.getFullYear() === gorunenAy?.yil &&
      t.getMonth() === gorunenAy?.ay &&
      t.getDate() === gun;
  };

  const buAnaYil = new Date().getFullYear();
  const yillar = Array.from({ length: (buAnaYil + 10) - 1920 + 1 }, (_, i) => 1920 + i);

  // Aydaki dolu gün sayısı (bilgi için)
  const doluGunSayisi = gorunenAy
    ? Array.from({ length: new Date(gorunenAy.yil, gorunenAy.ay + 1, 0).getDate() }, (_, i) => i + 1)
        .filter(g => isDolu(g)).length
    : 0;

  return (
    <>
      <div
        ref={triggerRef}
        onClick={handleOpen}
        className={`flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2 bg-gray-50 cursor-pointer hover:border-blue-400 hover:bg-white transition-all ${acik ? 'border-blue-400 ring-2 ring-blue-100 bg-white' : ''} ${className}`}
      >
        <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <span className={`text-sm flex-1 ${value ? 'text-gray-700 font-medium' : 'text-gray-400'}`}>
          {value || placeholder}
        </span>
      </div>

      {acik && gorunenAy && (
        <div
          ref={pickerRef}
          className="fixed bg-white border border-gray-200 rounded-2xl shadow-2xl z-[200] overflow-hidden"
          style={{ top: pos.top, left: pos.left, width: 290, animation: 'fadeIn 0.15s ease-out' }}
        >
          {/* Ay/Yıl navigasyon */}
          <div className="flex items-center justify-between px-4 py-3 bg-indigo-600">
            <button onClick={oncekiAy} className="w-7 h-7 flex items-center justify-center rounded-lg text-white hover:bg-white/20 transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/></svg>
            </button>
            <div className="flex items-center gap-2">
              <AySecici ay={gorunenAy.ay} onChange={ay => setGorunenAy(g => ({ ...g, ay }))} />
              <YilSecici yil={gorunenAy.yil} yillar={yillar} onChange={yil => setGorunenAy(g => ({ ...g, yil }))} />
            </div>
            <button onClick={sonrakiAy} className="w-7 h-7 flex items-center justify-center rounded-lg text-white hover:bg-white/20 transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/></svg>
            </button>
          </div>

          {/* Doluluk açıklaması (oda seçiliyse) */}
          {doluAraliklar.length > 0 && (
            <div className="flex items-center gap-3 px-4 py-2 bg-red-50 border-b border-red-100">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-400"></div>
                <span className="text-xs text-red-600 font-medium">Dolu ({doluGunSayisi} gün)</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <span className="text-xs text-blue-600 font-medium">Seçili</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-blue-100"></div>
                <span className="text-xs text-blue-600 font-medium">Bugün</span>
              </div>
            </div>
          )}

          {/* Gün başlıkları */}
          <div className="grid grid-cols-7 px-3 pt-2">
            {GUNLER.map(g => (
              <div key={g} className="text-center text-xs font-semibold text-gray-400 py-1">{g}</div>
            ))}
          </div>

          {/* Günler */}
          <div className="grid grid-cols-7 px-3 pb-2">
            {takvimGunleri().map((gun, i) => {
              const dolu = gun ? isDolu(gun) : false;
              const disabled = gun ? isDisabled(gun) : false;
              const secili = gun ? isSecili(gun) : false;
              const bugunMu = gun ? isToday(gun) : false;

              return (
                <div key={i} className="flex items-center justify-center p-0.5">
                  {gun ? (
                    <button
                      onClick={() => gunSec(gun)}
                      disabled={disabled}
                      title={dolu ? 'Bu tarih dolu' : ''}
                      className={`w-8 h-8 rounded-full text-sm font-medium transition-all relative
                        ${secili
                          ? 'bg-blue-600 text-white shadow-sm'
                          : dolu
                          ? 'bg-red-100 text-red-400 cursor-not-allowed line-through'
                          : disabled
                          ? 'text-gray-300 cursor-not-allowed'
                          : bugunMu
                          ? 'bg-blue-100 text-blue-700 font-bold'
                          : 'text-gray-700 hover:bg-blue-50 hover:text-blue-700'
                        }`}
                    >
                      {gun}
                    </button>
                  ) : <div className="w-8 h-8" />}
                </div>
              );
            })}
          </div>

          {/* Alt butonlar */}
          <div className="flex justify-between px-4 py-2 border-t border-gray-100">
            <button onClick={bugun} className="text-blue-600 hover:text-blue-700 text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-all">
              Bugün
            </button>
            {doluAraliklar.length === 0 && (
              <span className="text-xs text-gray-400 self-center">Oda seçilince doluluk görünür</span>
            )}
            <button onClick={() => setAcik(false)} className="text-gray-500 hover:text-gray-700 text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-all">
              Kapat
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </>
  );
}
